import { z } from "zod";
import { discordSnowflakeSchema, utcTimestampSchema, } from "./guild-settings.js";
import { MAX_ASSISTANT_MEMBER_IDS, MAX_ASSISTANT_ROLE_IDS, } from "./guild-selectors.js";
import { jsonUtf8ByteLength, normalizedSnowflakeListSchema, sanitizedJsonObjectSchema, unicodeCodePointLength, utf8ByteLength, uuidSchema, } from "./platform-common.js";
export const WOWAUDIT_ARGUMENT_CONTRACT_VERSION = 1;
export const MAX_WOWAUDIT_CREDENTIAL_UTF8_BYTES = 4_096;
export const MAX_WOWAUDIT_PROPOSAL_JSON_UTF8_BYTES = 16_384;
export const MAX_WOWAUDIT_RETAINED_FINGERPRINTS = 8;
export const WOWAUDIT_EXECUTION_LEASE_SECONDS = 120;
export const WOWAUDIT_CREDENTIAL_AAD_DOMAIN = "raidlens:wowaudit-credential:v1";
export const WOWAUDIT_CREDENTIAL_FINGERPRINT_DOMAIN = "raidlens:wowaudit-credential-fingerprint:v1";
export const wowauditAesKeyVersionSchema = z
    .string()
    .regex(/^[A-Za-z0-9][A-Za-z0-9_-]{0,31}$/);
export const wowauditFingerprintKeyVersionSchema = z
    .int()
    .positive()
    .max(2_147_483_647);
const wowauditFingerprintSchema = z.custom((value) => value instanceof Uint8Array && value.byteLength === 32, "Fingerprint must contain exactly 32 bytes");
export const wowauditCredentialFingerprintSchema = z
    .object({
    fingerprintKeyVersion: wowauditFingerprintKeyVersionSchema,
    keyFingerprint: wowauditFingerprintSchema,
})
    .strict();
export const wowauditCredentialFingerprintSetSchema = z
    .array(wowauditCredentialFingerprintSchema)
    .min(1)
    .max(MAX_WOWAUDIT_RETAINED_FINGERPRINTS)
    .refine((fingerprints) => new Set(fingerprints.map(({ fingerprintKeyVersion }) => fingerprintKeyVersion)).size === fingerprints.length, "Fingerprint key versions must be unique");
export const wowauditCredentialFingerprintSubmissionSchema = z
    .object({
    fingerprints: wowauditCredentialFingerprintSetSchema,
    activeFingerprintKeyVersion: wowauditFingerprintKeyVersionSchema,
    activeKeyFingerprint: wowauditFingerprintSchema,
})
    .strict()
    .refine(({ fingerprints, activeFingerprintKeyVersion, activeKeyFingerprint }) => fingerprints.some(({ fingerprintKeyVersion, keyFingerprint }) => fingerprintKeyVersion === activeFingerprintKeyVersion &&
    equalBytes(keyFingerprint, activeKeyFingerprint)), "Active fingerprint must be present in the retained fingerprint set");
export const wowauditTeamIdSchema = z
    .string()
    .min(1)
    .refine((value) => unicodeCodePointLength(value) <= 200, {
    message: "Team ID must contain at most 200 Unicode code points",
});
export const wowauditTeamDisplayNameSchema = z
    .string()
    .min(1)
    .refine((value) => unicodeCodePointLength(value) <= 200, {
    message: "Team display name must contain at most 200 Unicode code points",
})
    .refine((value) => utf8ByteLength(value) <= 1_024, {
    message: "Team display name must contain at most 1024 UTF-8 bytes",
});
export const wowauditTeamValidationOutputSchema = z
    .object({
    teamId: wowauditTeamIdSchema,
    teamDisplayName: wowauditTeamDisplayNameSchema,
})
    .strict();
export function encodeWowauditCredentialBytes(credential) {
    return new TextEncoder().encode(wowauditCredentialSchema.parse(credential));
}
export function encodeWowauditCredentialAad(guildId) {
    const parsedGuildId = discordSnowflakeSchema.parse(guildId);
    return new TextEncoder().encode(`${WOWAUDIT_CREDENTIAL_AAD_DOMAIN}\0${parsedGuildId}`);
}
export function encodeWowauditCredentialFingerprintInput(credentialBytes) {
    if (!(credentialBytes instanceof Uint8Array)) {
        throw new TypeError("Credential bytes must be a Uint8Array");
    }
    if (credentialBytes.byteLength < 1 ||
        credentialBytes.byteLength > MAX_WOWAUDIT_CREDENTIAL_UTF8_BYTES) {
        throw new RangeError("Credential byte length is invalid");
    }
    const prefix = new TextEncoder().encode(`${WOWAUDIT_CREDENTIAL_FINGERPRINT_DOMAIN}\0`);
    const input = new Uint8Array(prefix.byteLength + credentialBytes.byteLength);
    input.set(prefix);
    input.set(credentialBytes, prefix.byteLength);
    return input;
}
export const wowauditConnectionValidationStatusSchema = z.enum([
    "pending",
    "valid",
    "invalid",
    "revoked",
]);
export const wowauditWriteOperationSchema = z.enum([
    "wowaudit_track_character",
    "wowaudit_update_character",
    "wowaudit_create_raid",
    "wowaudit_update_raid",
    "wowaudit_upload_wishlist",
]);
export const wowauditWriteProposalStatusSchema = z.enum([
    "pending",
    "confirmed",
    "executing",
    "succeeded",
    "failed",
    "unknown",
    "expired",
    "cancelled",
]);
const wowauditRoleSchema = z.enum(["Melee", "Ranged", "Heal", "Tank"]);
const wowauditRankSchema = z.enum(["Main", "Trial", "Social", "Alt"]);
const wowauditDifficultySchema = z.enum([
    "Mythic",
    "Heroic",
    "Normal",
    "Raid Finder",
]);
const wowauditRaidStatusSchema = z.enum(["Planned", "Locked", "Cancelled"]);
const wowauditSignupStatusSchema = z.enum([
    "Present",
    "Absent",
    "Tentative",
    "Late",
    "Standby",
]);
const wowauditClassSchema = z.enum([
    "Warrior",
    "Paladin",
    "Hunter",
    "Rogue",
    "Priest",
    "Death Knight",
    "Shaman",
    "Mage",
    "Warlock",
    "Monk",
    "Druid",
    "Demon Hunter",
    "Evoker",
]);
const positiveIdSchema = z.int().positive();
const nonEmptyArgumentTextSchema = z.string().min(1);
const raidTimeSchema = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/);
const uniquePositiveIdListSchema = z
    .array(positiveIdSchema)
    .max(100)
    .refine((values) => new Set(values).size === values.length, {
    message: "IDs must be unique",
});
function boundedArgumentSchema(schema) {
    return schema.refine((value) => jsonUtf8ByteLength(value) <= MAX_WOWAUDIT_PROPOSAL_JSON_UTF8_BYTES, `Arguments must contain at most ${MAX_WOWAUDIT_PROPOSAL_JSON_UTF8_BYTES} UTF-8 bytes`);
}
export const wowauditTrackCharacterArgumentsV1Schema = boundedArgumentSchema(z
    .object({
    name: nonEmptyArgumentTextSchema,
    realm: nonEmptyArgumentTextSchema,
    role: wowauditRoleSchema.optional(),
    spec: nonEmptyArgumentTextSchema.optional(),
    rank: wowauditRankSchema.optional(),
    note: nonEmptyArgumentTextSchema.optional(),
})
    .strict());
export const wowauditUpdateCharacterArgumentsV1Schema = boundedArgumentSchema(z
    .object({
    id: positiveIdSchema,
    role: wowauditRoleSchema.optional(),
    spec: nonEmptyArgumentTextSchema.optional(),
    rank: wowauditRankSchema.optional(),
    note: nonEmptyArgumentTextSchema.optional(),
})
    .strict()
    .refine((value) => value.role !== undefined ||
    value.spec !== undefined ||
    value.rank !== undefined ||
    value.note !== undefined, "Character update must contain a change"));
export const wowauditCreateRaidArgumentsV1Schema = boundedArgumentSchema(z
    .object({
    date: z.iso.date(),
    startTime: raidTimeSchema,
    endTime: raidTimeSchema,
    instance: nonEmptyArgumentTextSchema.optional(),
    entireSeason: z.boolean().optional(),
    optional: z.boolean().optional(),
    difficulty: wowauditDifficultySchema.optional(),
})
    .strict());
const wowauditEncounterChangeSchema = z
    .object({
    encounter_id: positiveIdSchema,
    role: wowauditRoleSchema.optional(),
    class: wowauditClassSchema.optional(),
    selected: z.boolean().optional(),
})
    .strict();
const wowauditSignupChangeSchema = z
    .object({
    character_id: positiveIdSchema,
    status: wowauditSignupStatusSchema.optional(),
    role: wowauditRoleSchema.optional(),
    class: wowauditClassSchema.optional(),
    comment: z.string().optional(),
    selected: z.boolean().optional(),
    encounter_changes: z
        .array(wowauditEncounterChangeSchema)
        .max(100)
        .refine((values) => new Set(values.map(({ encounter_id }) => encounter_id)).size ===
        values.length, "Encounter changes must have unique encounter IDs")
        .optional(),
})
    .strict();
export const wowauditUpdateRaidArgumentsV1Schema = boundedArgumentSchema(z
    .object({
    id: positiveIdSchema,
    status: wowauditRaidStatusSchema.optional(),
    startTime: raidTimeSchema.optional(),
    endTime: raidTimeSchema.optional(),
    optional: z.boolean().optional(),
    difficulty: wowauditDifficultySchema.optional(),
    enableEncounters: uniquePositiveIdListSchema.optional(),
    disableEncounters: uniquePositiveIdListSchema.optional(),
    signupChanges: z
        .array(wowauditSignupChangeSchema)
        .max(100)
        .refine((values) => new Set(values.map(({ character_id }) => character_id)).size ===
        values.length, "Signup changes must have unique character IDs")
        .optional(),
})
    .strict()
    .superRefine((value, context) => {
    const changed = Object.entries(value).some(([key, fieldValue]) => key !== "id" && fieldValue !== undefined);
    if (!changed) {
        context.addIssue({
            code: "custom",
            message: "Raid update must contain a change",
        });
    }
    const disabled = new Set(value.disableEncounters ?? []);
    if ((value.enableEncounters ?? []).some((id) => disabled.has(id))) {
        context.addIssue({
            code: "custom",
            message: "An encounter cannot be enabled and disabled together",
        });
    }
}));
const wishlistBase = {
    reportId: nonEmptyArgumentTextSchema,
    configurationName: nonEmptyArgumentTextSchema,
    replaceManualEdits: z.boolean().optional(),
    clearConduits: z.boolean().optional(),
};
export const wowauditUploadWishlistArgumentsV1Schema = boundedArgumentSchema(z
    .object({
    ...wishlistBase,
    characterId: positiveIdSchema.optional(),
    characterName: nonEmptyArgumentTextSchema.optional(),
})
    .strict()
    .refine((value) => value.characterId !== undefined || value.characterName !== undefined, "Wishlist upload requires a character ID or character name"));
export const wowauditWriteArgumentsV1Schema = z.discriminatedUnion("operation", [
    z
        .object({
        operation: z.literal("wowaudit_track_character"),
        argumentContractVersion: z.literal(WOWAUDIT_ARGUMENT_CONTRACT_VERSION),
        arguments: wowauditTrackCharacterArgumentsV1Schema,
    })
        .strict(),
    z
        .object({
        operation: z.literal("wowaudit_update_character"),
        argumentContractVersion: z.literal(WOWAUDIT_ARGUMENT_CONTRACT_VERSION),
        arguments: wowauditUpdateCharacterArgumentsV1Schema,
    })
        .strict(),
    z
        .object({
        operation: z.literal("wowaudit_create_raid"),
        argumentContractVersion: z.literal(WOWAUDIT_ARGUMENT_CONTRACT_VERSION),
        arguments: wowauditCreateRaidArgumentsV1Schema,
    })
        .strict(),
    z
        .object({
        operation: z.literal("wowaudit_update_raid"),
        argumentContractVersion: z.literal(WOWAUDIT_ARGUMENT_CONTRACT_VERSION),
        arguments: wowauditUpdateRaidArgumentsV1Schema,
    })
        .strict(),
    z
        .object({
        operation: z.literal("wowaudit_upload_wishlist"),
        argumentContractVersion: z.literal(WOWAUDIT_ARGUMENT_CONTRACT_VERSION),
        arguments: wowauditUploadWishlistArgumentsV1Schema,
    })
        .strict(),
]);
const configuredConnectionStatusBase = {
    guildId: discordSnowflakeSchema,
    connectionState: z.literal("configured"),
    credentialRevision: z.int().positive(),
    updatedAt: utcTimestampSchema,
};
export const wowauditValidConnectionStatusSchema = z
    .object({
    ...configuredConnectionStatusBase,
    validationStatus: z.literal("valid"),
    teamDisplayName: wowauditTeamDisplayNameSchema,
    validatedAt: utcTimestampSchema,
})
    .strict();
export const wowauditConfiguredConnectionStatusSchema = z.discriminatedUnion("validationStatus", [
    wowauditValidConnectionStatusSchema,
    z
        .object({
        ...configuredConnectionStatusBase,
        validationStatus: z.literal("pending"),
        teamDisplayName: z.null(),
        validatedAt: z.null(),
    })
        .strict(),
    z
        .object({
        ...configuredConnectionStatusBase,
        validationStatus: z.literal("invalid"),
        teamDisplayName: z.null(),
        validatedAt: z.null(),
    })
        .strict(),
    z
        .object({
        ...configuredConnectionStatusBase,
        validationStatus: z.literal("revoked"),
        teamDisplayName: z.null(),
        validatedAt: z.null(),
    })
        .strict(),
]);
export const wowauditConnectionStatusSchema = z.union([
    z
        .object({
        guildId: discordSnowflakeSchema,
        connectionState: z.literal("not_configured"),
    })
        .strict(),
    wowauditConfiguredConnectionStatusSchema,
]);
export const wowauditCredentialSchema = z
    .string()
    .min(1)
    .refine((value) => utf8ByteLength(value) <= MAX_WOWAUDIT_CREDENTIAL_UTF8_BYTES, `Credential must contain at most ${MAX_WOWAUDIT_CREDENTIAL_UTF8_BYTES} UTF-8 bytes`);
export const wowauditCredentialSubmissionSchema = z
    .object({
    credential: wowauditCredentialSchema,
    expectedCredentialRevision: z.int().positive().nullable(),
})
    .strict();
export const wowauditCredentialRevocationSchema = z
    .object({ expectedCredentialRevision: z.int().positive() })
    .strict();
export const wowauditCredentialSubmissionResultSchema = wowauditConnectionStatusSchema;
export const wowauditCredentialRevocationResultSchema = wowauditConnectionStatusSchema;
const writeRoleIdsSchema = normalizedSnowflakeListSchema(MAX_ASSISTANT_ROLE_IDS, "WoWAudit write role IDs");
const writeMemberIdsSchema = normalizedSnowflakeListSchema(MAX_ASSISTANT_MEMBER_IDS, "WoWAudit write member IDs");
export const wowauditWriteAccessSchema = z
    .object({
    guildId: discordSnowflakeSchema,
    allowedRoleIds: writeRoleIdsSchema,
    allowedMemberIds: writeMemberIdsSchema,
    revision: z.int().positive(),
    updatedAt: utcTimestampSchema,
})
    .strict();
export const wowauditWriteAccessReplacementSchema = z
    .object({
    allowedRoleIds: writeRoleIdsSchema,
    allowedMemberIds: writeMemberIdsSchema,
    expectedRevision: z.int().positive(),
})
    .strict();
const proposalTargetSummarySchema = sanitizedJsonObjectSchema(MAX_WOWAUDIT_PROPOSAL_JSON_UTF8_BYTES);
const outcomeCodeSchema = z
    .string()
    .regex(/^[a-z][a-z0-9_]{0,63}$/)
    .nullable();
const completionReasonSchema = z
    .string()
    .min(1)
    .refine((value) => unicodeCodePointLength(value) <= 500, {
    message: "Completion reason must contain at most 500 Unicode code points",
})
    .refine((value) => utf8ByteLength(value) <= 2_048, {
    message: "Completion reason must contain at most 2048 UTF-8 bytes",
})
    .nullable();
export const wowauditWriteProposalCreateSchema = z
    .object({
    guildId: discordSnowflakeSchema,
    requesterUserId: discordSnowflakeSchema,
    write: wowauditWriteArgumentsV1Schema,
    targetSummary: proposalTargetSummarySchema,
    credentialRevision: z.int().positive(),
    writeAccessRevision: z.int().positive(),
    correlationId: uuidSchema,
    idempotencyKey: z
        .string()
        .min(1)
        .refine((value) => unicodeCodePointLength(value) <= 200, {
        message: "Idempotency key must contain at most 200 Unicode code points",
    })
        .nullable(),
})
    .strict();
export const wowauditProposalConfirmAndClaimSchema = z
    .object({
    proposalId: uuidSchema,
    guildId: discordSnowflakeSchema,
    confirmedByUserId: discordSnowflakeSchema,
    currentRoleIds: writeRoleIdsSchema,
    expectedWriteAccessRevision: z.int().positive(),
    expectedCredentialRevision: z.int().positive(),
    write: wowauditWriteArgumentsV1Schema,
    confirmationChannelId: discordSnowflakeSchema,
    confirmationMessageId: discordSnowflakeSchema,
    confirmationInteractionId: discordSnowflakeSchema,
    claimOwner: uuidSchema,
})
    .strict();
export const wowauditProposalExecutionClaimSchema = z
    .object({
    proposalId: uuidSchema,
    status: z.literal("executing"),
    write: wowauditWriteArgumentsV1Schema,
    claimOwner: uuidSchema,
    claimedAt: utcTimestampSchema,
    leaseExpiresAt: utcTimestampSchema,
    dispatchStartedAt: utcTimestampSchema.nullable(),
    reclaimed: z.boolean(),
})
    .strict();
export const wowauditProposalMarkDispatchStartedSchema = z
    .object({ proposalId: uuidSchema, claimOwner: uuidSchema })
    .strict();
export const wowauditProposalDispatchStartedSchema = z
    .object({
    proposalId: uuidSchema,
    dispatchStartedAt: utcTimestampSchema,
    dispatchDeadlineAt: utcTimestampSchema,
})
    .strict();
export const wowauditProposalRecoveryResultSchema = z
    .object({
    pendingExpired: z.int().min(0).max(500),
    confirmedExpired: z.int().min(0).max(500),
    predispatchExpired: z.int().min(0).max(500),
    postdispatchUnknown: z.int().min(0).max(500),
    totalRecovered: z.int().min(0).max(500),
})
    .strict()
    .refine((value) => value.totalRecovered ===
    value.pendingExpired +
        value.confirmedExpired +
        value.predispatchExpired +
        value.postdispatchUnknown, "Recovery total must equal the lifecycle counts");
export const wowauditProposalCompletionSchema = z
    .object({
    proposalId: uuidSchema,
    claimOwner: uuidSchema,
    resultStatus: z.enum(["succeeded", "failed", "unknown"]),
    outcomeCode: outcomeCodeSchema,
    completionReason: completionReasonSchema,
})
    .strict();
export const wowauditProposalCancellationSchema = z
    .object({
    proposalId: uuidSchema,
    guildId: discordSnowflakeSchema,
    cancelledByUserId: discordSnowflakeSchema,
})
    .strict();
export const wowauditWriteProposalStatusDisplaySchema = z
    .object({
    proposalId: uuidSchema,
    guildId: discordSnowflakeSchema,
    operation: wowauditWriteOperationSchema,
    argumentContractVersion: z.literal(WOWAUDIT_ARGUMENT_CONTRACT_VERSION),
    targetSummary: proposalTargetSummarySchema,
    status: wowauditWriteProposalStatusSchema,
    createdAt: utcTimestampSchema,
    expiresAt: utcTimestampSchema,
    confirmedAt: utcTimestampSchema.nullable(),
    claimedAt: utcTimestampSchema.nullable(),
    dispatchStartedAt: utcTimestampSchema.nullable(),
    completedAt: utcTimestampSchema.nullable(),
    outcomeCode: outcomeCodeSchema,
})
    .strict();
export const wowauditWriteProposalStoredAuthorizationSchema = z
    .object({
    storedActorAuthorized: z.boolean(),
    proposal: wowauditWriteProposalStatusDisplaySchema.nullable(),
})
    .strict()
    .refine(({ storedActorAuthorized, proposal }) => !storedActorAuthorized || proposal !== null, "Stored proposal authorization requires a proposal");
export const botWowauditConfirmationContextSchema = z
    .object({
    guildId: discordSnowflakeSchema,
    installationActive: z.boolean(),
    entitlement: z
        .object({
        effectiveState: z.enum([
            "trial",
            "subscribed",
            "unsubscribed",
            "complimentary",
        ]),
        revision: z.int().positive(),
    })
        .strict(),
    linkedLeaderUserId: discordSnowflakeSchema.nullable(),
    writeAccess: z
        .object({
        allowedRoleIds: writeRoleIdsSchema,
        allowedMemberIds: writeMemberIdsSchema,
        revision: z.int().positive(),
    })
        .strict()
        .nullable(),
    credential: z
        .object({
        validationStatus: wowauditConnectionValidationStatusSchema,
        credentialRevision: z.int().positive(),
        boundTeamId: wowauditTeamIdSchema.nullable(),
        validatedTeamId: wowauditTeamIdSchema.nullable(),
    })
        .strict()
        .refine(({ validationStatus, boundTeamId, validatedTeamId }) => validationStatus !== "valid" ||
        (boundTeamId !== null && validatedTeamId === boundTeamId), "Valid credential metadata must match the bound team")
        .nullable(),
    proposal: z
        .object({
        proposalId: uuidSchema,
        requesterUserId: discordSnowflakeSchema,
        write: wowauditWriteArgumentsV1Schema,
        credentialRevision: z.int().positive(),
        status: wowauditWriteProposalStatusSchema,
        expiresAt: utcTimestampSchema,
    })
        .strict(),
})
    .strict();
export const wowauditWriteProposalDisplaySchema = wowauditWriteProposalStatusDisplaySchema;
function equalBytes(left, right) {
    if (left.byteLength !== right.byteLength)
        return false;
    let difference = 0;
    for (let index = 0; index < left.byteLength; index += 1) {
        difference |= left[index] ^ right[index];
    }
    return difference === 0;
}
//# sourceMappingURL=wowaudit.js.map